import React, { useEffect, useRef, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import {
  DISC_COLORS,
  DISC_LABELS,
  DISC_TO_ARTS,
  ARTS_ORDER,
  COMPETENCIA_IDEAL,
  COMPETENCIA_ACTUAL,
  getCompetenciaDiscFactor,
  getDominantFactor,
  computeGapData,
  getHeatmapColorByScore,
  computeGroupHeatmapData,
  largestRemainderRound,
  hexToRgba,
  getDiscWheelOpacity,
  getDiscWheelLabelColor,
  getDiscWheelLabelStrokeColor,
} from './DISCConstants';

// Stripe pattern used by heatmap cells and legend swatches.
// Inline SVG data URI (NOT repeating-linear-gradient) because html2canvas 1.4.1
// fails to render CSS gradients reliably — but treats data URIs as images
// and captures them perfectly in PNG/PDF export.
const HEATMAP_STRIPE_BG = `url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10'%3E%3Cpath d='M-1,9 L1,11 M0,0 L10,10 M9,-1 L11,1' stroke='white' stroke-width='3' stroke-opacity='0.35' fill='none'/%3E%3C/svg%3E")`;
import Loader from '../../../components/common/Loader';
import CompatibilidadCompetenciasPanel from './CompatibilidadCompetenciasPanel';
import { getArtsColors } from '../../../arts/arts-constants';
import { ARTSPreviewDonut } from '../../../arts/components/ARTSPreview/ARTSChartsPreview';
import '../../../arts/components/ARTSPreview/ARTSPreview.scss';

// ── Helpers ───────────────────────────────────────────────────────────

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const DISC_LABEL_KEYS = {
  D: 'toolbox.discPanel.labels.assertiveness',
  I: 'toolbox.discPanel.labels.sociability',
  S: 'toolbox.discPanel.labels.tolerance',
  C: 'toolbox.discPanel.labels.rules',
};

function getDiscLabel(key) {
  const i18nKey = DISC_LABEL_KEYS[key];
  return i18nKey ? I18n.t(i18nKey) : DISC_LABELS[key];
}

// Map backend Spanish keys to internal D/I/S/C
// Asertividad → D, Sociabilidad → I, Tolerancia → S, Reglas → C
function mapBackendValues(values) {
  if (!values) return { D: 0, I: 0, S: 0, C: 0 };
  return {
    D: values.asertividad ?? 0,
    I: values.sociabilidad ?? 0,
    S: values.tolerancia ?? 0,
    C: values.reglas ?? 0,
  };
}

function discEntryForPerson(person, discProfiles) {
  if (!discProfiles || !discProfiles.length) return null;
  return (
    discProfiles.find(
      (entry) =>
        entry.id_results === person.id_results ||
        entry.id_results === person.id_result,
    ) || null
  );
}

function discProfileForPerson(person, discProfiles) {
  const entry = discEntryForPerson(person, discProfiles);
  return mapBackendValues(entry?.values);
}

const DISC_WHEEL_SEGMENTS = [
  {
    key: 'I',
    path: 'M 100 100 L 100 0 A 100 100 0 0 1 200 100 Z',
    letterPosition: { x: 146, y: 50 },
    percentagePosition: { x: 146, y: 68 },
  },
  {
    key: 'S',
    path: 'M 100 100 L 200 100 A 100 100 0 0 1 100 200 Z',
    letterPosition: { x: 146, y: 150 },
    percentagePosition: { x: 146, y: 132 },
  },
  {
    key: 'C',
    path: 'M 100 100 L 100 200 A 100 100 0 0 1 0 100 Z',
    letterPosition: { x: 54, y: 150 },
    percentagePosition: { x: 54, y: 132 },
  },
  {
    key: 'D',
    path: 'M 100 100 L 0 100 A 100 100 0 0 1 100 0 Z',
    letterPosition: { x: 54, y: 50 },
    percentagePosition: { x: 54, y: 68 },
  },
];

function normalizeDiscValue(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

// ── DISC Wheel (SVG — donut style) ───────────────────────────────────

function DISCWheel({ profile, size = 200 }) {
  const segments = DISC_WHEEL_SEGMENTS.map((segment) => {
    const value = normalizeDiscValue(profile?.[segment.key]);
    const textColor = getDiscWheelLabelColor();

    return {
      ...segment,
      value,
      opacity: getDiscWheelOpacity(value),
      textColor,
      textStroke: getDiscWheelLabelStrokeColor(),
    };
  });

  return (
    <div className="disc-wheel-sizer" style={{ width: size, height: size }}>
      <svg viewBox="0 0 200 200" className="disc-wheel-svg">
        {/* NW-2543 ASTR layout: A (top-left, D), S (top-right, I), R (bot-left, C), T (bot-right, S). */}
        {segments.map((segment) => (
          <path
            key={`disc-wheel-segment-${segment.key}`}
            d={segment.path}
            fill={DISC_COLORS[segment.key]}
            opacity={segment.opacity}
          />
        ))}
        <circle cx="100" cy="100" r="30" fill="white" />
        {/* Quadrant labels: ARTS letter + percentage share one adaptive contrast rule. */}
        {segments.map((segment) => (
          <text
            key={`disc-wheel-letter-${segment.key}`}
            x={segment.letterPosition.x}
            y={segment.letterPosition.y}
            textAnchor="middle"
            dominantBaseline="central"
            fill={segment.textColor}
            stroke={segment.textStroke}
            strokeWidth="0.9"
            strokeLinejoin="round"
            paintOrder="stroke"
            fontFamily="Poppins, sans-serif"
            fontSize="18"
            fontWeight="700"
          >
            {DISC_TO_ARTS[segment.key]}
          </text>
        ))}
        {segments.map((segment) => (
          <text
            key={`disc-wheel-pct-${segment.key}`}
            x={segment.percentagePosition.x}
            y={segment.percentagePosition.y}
            textAnchor="middle"
            dominantBaseline="central"
            fill={segment.textColor}
            stroke={segment.textStroke}
            strokeWidth="0.7"
            strokeLinejoin="round"
            paintOrder="stroke"
            fontFamily="Poppins, sans-serif"
            fontSize="12"
            fontWeight="600"
          >
            {segment.value}%
          </text>
        ))}
      </svg>
    </div>
  );
}

// ── GapChart (single-user, competencias + puesto) ─────────────────────

function GapTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;

  const item = payload[0]?.payload;
  if (!item || !item.fullName) return null;

  const factor = item.factor;
  const factorColor = item.color || (factor ? DISC_COLORS[factor] : null);
  const gap = item.gap;
  const gapColor = gap >= 0 ? '#22c55e' : '#ef4444';

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '10px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        fontFamily: 'Poppins, sans-serif',
        fontSize: '12px',
        maxWidth: '260px',
      }}
    >
      <div style={{ fontWeight: 600, color: '#2b2b2b', marginBottom: '8px' }}>
        {item.fullName}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          columnGap: '12px',
          rowGap: '4px',
          marginBottom: '8px',
        }}
      >
        <span style={{ color: '#888' }}>{I18n.t('toolbox.discPanel.gapChart.actual')}:</span>
        <span style={{ color: '#2b2b2b', fontWeight: 500 }}>{item.actual}%</span>

        <span style={{ color: '#888' }}>{I18n.t('toolbox.discPanel.gapChart.ideal')}:</span>
        <span style={{ color: '#2b2b2b', fontWeight: 500 }}>{item.ideal}%</span>
      </div>

      {factor && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: factorColor,
              display: 'inline-block',
              flexShrink: 0,
            }}
          />
          <span style={{ color: '#888' }}>{getDiscLabel(factor)}</span>
        </div>
      )}

      {gap !== 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '12px' }}>
          <span style={{ color: '#888' }}>
            {gap > 0
              ? I18n.t('toolbox.discPanel.gapChart.exceeds')
              : I18n.t('toolbox.discPanel.gapChart.gap')}
            :
          </span>
          <span style={{ textAlign: 'right', fontWeight: 600, color: gapColor }}>
            {gap > 0 ? `+${gap}` : gap}
          </span>
        </div>
      )}
    </div>
  );
}

function GapChart({ person, selectedCompetencias, selectedPuestos, discGap }) {
  const puestoName = selectedPuestos.length > 0 ? selectedPuestos[0].title : '';

  // Loading or no data yet — render nothing to avoid layout shift
  if (!discGap || !discGap.persons) {
    return null;
  }

  // Find this person's data using id_results (NOT person.id which is the userId)
  const personData = discGap.persons.find(
    (p) => p.id_results === person.id_results,
  );

  // Inconclusive profile — render fallback message
  if (personData && personData.is_inconclusive) {
    return (
      <div className="disc-chart-card disc-gap-section">
        <div className="disc-chart-title">{I18n.t('toolbox.discPanel.gapChart.title')}</div>
        <div className="disc-gap-empty">
          {I18n.t('toolbox.discPanel.gapChart.inconclusiveProfile')}
        </div>
      </div>
    );
  }

  // Person not found in response yet — render nothing
  if (!personData || !personData.competencias) {
    return null;
  }

  const language = localStorage.getItem('language') || 'es';
  const nameKey = language === 'en' ? 'name_en' : language === 'pt' ? 'name_pt' : 'name_es';

  const gapData = personData.competencias.map((comp) => {
    const compName = comp[nameKey] || comp.name_es;
    const factor = getCompetenciaDiscFactor(comp.name_es);
    return {
      name: compName.length > 20 ? compName.substring(0, 20) + '…' : compName,
      fullName: compName,
      actual: comp.actual,
      ideal: comp.ideal,
      gap: comp.gap,
      posGap: comp.gap > 0 ? comp.gap : 0,
      negGap: comp.gap < 0 ? comp.gap : 0,
      factor,
      color: DISC_COLORS[factor] || '#773db0',
    };
  });

  // Domain for X-axis
  const gaps = gapData.map((d) => d.gap);
  const minGap = Math.min(...gaps, 0);
  const maxGap = Math.max(...gaps, 0);
  const step = 7;
  const domain = [
    Math.floor((minGap - step) / step) * step,
    Math.ceil((maxGap + step) / step) * step,
  ];

  const chartHeight = Math.max(260, gapData.length * 55);

  return (
    <div className="disc-chart-card disc-gap-section">
      <div className="disc-chart-title">{I18n.t('toolbox.discPanel.gapChart.title')}</div>
      {puestoName && (
        <div className="disc-gap-subtitle">
          {person.title} vs. {puestoName}
        </div>
      )}

      {/* Legend — gap types (striped swatches) */}
      <div className="disc-gap-legend-types">
        <div className="disc-gap-legend-item">
          <svg width="12" height="12" className="disc-gap-legend-swatch">
            <defs>
              <pattern id="legend-pos" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="4" height="4" fill="#22c55e" />
                <line x1="0" y1="0" x2="0" y2="4" stroke="rgba(255,255,255,0.4)" strokeWidth="2" />
              </pattern>
            </defs>
            <rect width="12" height="12" fill="url(#legend-pos)" />
          </svg>
          <span>{I18n.t('toolbox.discPanel.gapChart.aboveIdeal')}</span>
        </div>
        <div className="disc-gap-legend-item">
          <svg width="12" height="12" className="disc-gap-legend-swatch">
            <defs>
              <pattern id="legend-neg" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="4" height="4" fill="#ef4444" />
                <line x1="0" y1="0" x2="0" y2="4" stroke="rgba(255,255,255,0.4)" strokeWidth="2" />
              </pattern>
            </defs>
            <rect width="12" height="12" fill="url(#legend-neg)" />
          </svg>
          <span>{I18n.t('toolbox.discPanel.gapChart.gapToDevelop')}</span>
        </div>
      </div>

      {/* Legend — DISC factor colors */}
      <div className="disc-gap-legend-factors">
        {ARTS_ORDER.map((key) => (
          <div key={key} className="disc-gap-factor-item">
            <span className="disc-gap-factor-dot" style={{ backgroundColor: DISC_COLORS[key] }} />
            <span>{getDiscLabel(key)}</span>
          </div>
        ))}
      </div>

      {/* Diverging bar chart */}
      <ResponsiveContainer width="100%" height={chartHeight} minHeight={1}>
        <BarChart layout="vertical" data={gapData} margin={{ top: 5, right: 30, bottom: 5, left: 10 }}>
          <defs>
            <pattern id="gap-neg-stripes" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#ef4444" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(255,255,255,0.35)" strokeWidth="2.5" />
            </pattern>
            <pattern id="gap-pos-stripes" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#22c55e" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(255,255,255,0.35)" strokeWidth="2.5" />
            </pattern>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#ececf0" vertical horizontal={false} />
          <XAxis
            type="number"
            domain={domain}
            tick={{ fontFamily: 'Poppins, sans-serif', fontSize: 11, fill: '#aaa' }}
            tickFormatter={(val) => (val > 0 ? `+${val}` : String(val))}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={200}
            tick={({ x, y, payload }) => {
              const entry = gapData.find((d) => d.name === payload.value);
              const dotColor = entry?.color || '#333';
              const fullLabel = String(payload.value);
              const MAX_CHARS = 32;
              const displayLabel = fullLabel.length > MAX_CHARS
                ? `${fullLabel.slice(0, MAX_CHARS - 1)}…`
                : fullLabel;
              return (
                <g transform={`translate(${x - 8},${y})`}>
                  <circle cx="-190" cy="0" r="4" fill={dotColor} />
                  <text
                    x="-181"
                    y="0"
                    textAnchor="start"
                    dominantBaseline="central"
                    fontFamily="Poppins, sans-serif"
                    fontSize={11}
                    fill="#2b2b2b"
                  >
                    {displayLabel !== fullLabel && <title>{fullLabel}</title>}
                    {displayLabel}
                  </text>
                </g>
              );
            }}
          />
          <ReferenceLine x={0} stroke="#2b2b2b" strokeWidth={1} />
          <Tooltip content={<GapTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
          <Bar dataKey="posGap" fill="url(#gap-pos-stripes)" barSize={20} radius={[0, 4, 4, 0]} stackId="gap" />
          <Bar dataKey="negGap" fill="url(#gap-neg-stripes)" barSize={20} radius={[4, 0, 0, 4]} stackId="gap" />
        </BarChart>
      </ResponsiveContainer>

      {/* Detail table */}
      <div className="disc-gap-table">
        <div className="disc-gap-table-header">
          <div>{I18n.t('toolbox.discPanel.gapChart.competence')}</div>
          <div className="disc-gap-table-center">{I18n.t('toolbox.discPanel.gapChart.actual')}</div>
          <div className="disc-gap-table-center">{I18n.t('toolbox.discPanel.gapChart.ideal')}</div>
          <div className="disc-gap-table-center">{I18n.t('toolbox.discPanel.gapChart.gapColumn')}</div>
        </div>
        {gapData.map((entry) => (
          <div key={entry.fullName} className="disc-gap-table-row">
            <div className="disc-gap-table-comp" title={entry.fullName}>
              <span className="disc-gap-table-dot" style={{ backgroundColor: entry.color }} />
              <span className="disc-gap-table-name">{entry.fullName}</span>
            </div>
            <div className="disc-gap-table-center">{entry.actual}%</div>
            <div className="disc-gap-table-center">{entry.ideal}%</div>
            <div
              className="disc-gap-table-center disc-gap-table-gap"
              style={{ color: entry.gap >= 0 ? '#22c55e' : '#ef4444' }}
            >
              {entry.gap > 0 ? `+${entry.gap}` : entry.gap}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SingleUserView ────────────────────────────────────────────────────

function SingleUserView({ person, selectedCompetencias, selectedPuestos, discProfiles, discGap, wheelSize = 200, hideGapChart = false }) {
  const discEntry = discEntryForPerson(person, discProfiles);
  const profile = mapBackendValues(discEntry?.values);
  const profileName = discEntry?.profile_name;

  const barData = ARTS_ORDER.map((key) => ({
    name: key,
    fullLabel: getDiscLabel(key),
    value: profile[key] || 0,
    color: DISC_COLORS[key],
  }));

  const showGapChart = !hideGapChart && selectedPuestos.length > 0 && selectedCompetencias.length > 0;

  return (
    <div className="disc-single-view">
      {/* User header card */}
      <div className="disc-user-card">
        <div className="disc-avatar">
          {getInitials(person.title)}
        </div>
        <div className="disc-user-info">
          <div className="disc-user-name">{person.title}</div>
          {profileName && (
            <div className="disc-user-role">{profileName}</div>
          )}
          {person.caption?.description && (
            <div className="disc-user-campaign">{person.caption.description}</div>
          )}
        </div>
      </div>

      {/* Jerarquía Conductual (Bar) */}
      <div className="disc-chart-card disc-bar-card">
        <div className="disc-chart-title">{I18n.t('toolbox.discPanel.charts.behavioralHierarchy')}</div>
        <ResponsiveContainer width="100%" height={200} minHeight={1}>
          <BarChart layout="vertical" data={barData} margin={{ top: 5, right: 16, bottom: 5, left: 5 }}>
            <XAxis
              type="number"
              domain={[0, 100]}
              tick={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, fill: '#717182' }}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={24}
              tick={({ x, y, payload }) => (
                <text
                  x={x}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="central"
                  fill={DISC_COLORS[payload.value] || '#333'}
                  fontSize={14}
                  fontWeight={700}
                  fontFamily="Poppins, sans-serif"
                >
                  {DISC_TO_ARTS[payload.value] || payload.value}
                </text>
              )}
            />
            <Tooltip
              formatter={(value, _name, props) => [`${value}%`, props.payload.fullLabel]}
              contentStyle={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '12px',
                borderRadius: '10px',
                border: '1px solid rgba(0, 0, 0, 0.1)',
              }}
            />
            <Bar dataKey="value" radius={[0, 8, 8, 0]} barSize={28}>
              {barData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Rueda DISC */}
      <div className="disc-chart-card disc-wheel-section">
        <div className="disc-chart-title">{I18n.t('toolbox.discPanel.charts.discWheel')}</div>
        <div className="disc-wheel-wrapper">
          <DISCWheel profile={profile} size={wheelSize} />
        </div>
      </div>

      {/* Compatibilidad de Competencias (NW-2499) */}
      <CompatibilidadCompetenciasPanel
        selectedPersons={[person]}
        selectedCompetencias={selectedCompetencias}
        discProfiles={discProfiles}
      />

      {/* Gap Chart (competencias seleccionadas) */}
      {showGapChart && (
        <GapChart
          person={person}
          selectedCompetencias={selectedCompetencias}
          selectedPuestos={selectedPuestos}
          discGap={discGap}
        />
      )}
    </div>
  );
}

// ── MultiUserColumn ───────────────────────────────────────────────────

function MultiUserColumn({ person, discProfiles, selectedCompetencias }) {
  const profile = discProfileForPerson(person, discProfiles);
  const dominant = getDominantFactor(profile);

  return (
    <div className="disc-multi-column">
      {/* Mini user header */}
      <div className="disc-user-card disc-user-card--compact">
        <div className="disc-avatar disc-avatar--sm">
          {getInitials(person.title)}
        </div>
        <div className="disc-user-info">
          <div className="disc-user-name disc-user-name--sm">{person.title}</div>
          <div className="disc-user-role">{person.subtitle?.description}</div>
          {person.caption?.description && (
            <div className="disc-user-campaign">{person.caption.description}</div>
          )}
        </div>
      </div>

      {/* Charts area */}
      <div className="disc-multi-charts">
        {/* Jerarquía Conductual — HTML bars */}
        <div className="disc-chart-card disc-chart-card--compact">
          <div className="disc-chart-title disc-chart-title--sm">{I18n.t('toolbox.discPanel.charts.behavioralHierarchy')}</div>
          <div className="disc-mini-bars">
            {ARTS_ORDER.map((key) => (
              <div key={key} className="disc-mini-bar-row">
                <span
                  className="disc-mini-bar-label"
                  style={{ color: DISC_COLORS[key] }}
                >
                  {DISC_TO_ARTS[key] || key}
                </span>
                <div className="disc-mini-bar-track">
                  <div
                    className="disc-mini-bar-fill"
                    style={{
                      width: `${profile[key] || 0}%`,
                      backgroundColor: DISC_COLORS[key],
                    }}
                  />
                </div>
                <span className="disc-mini-bar-value">
                  {profile[key] || 0}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Wheel mini */}
        <div className="disc-chart-card disc-chart-card--compact disc-wheel-section">
          <div className="disc-chart-title disc-chart-title--sm">{I18n.t('toolbox.discPanel.charts.discWheel')}</div>
          <div className="disc-wheel-wrapper disc-wheel-wrapper--sm">
            <DISCWheel profile={profile} size={160} />
          </div>
        </div>

        {/* Compatibilidad de Competencias (NW-2499) */}
        <CompatibilidadCompetenciasPanel
          selectedPersons={[person]}
          selectedCompetencias={selectedCompetencias}
          discProfiles={discProfiles}
          compact
        />
      </div>
    </div>
  );
}

// ── GroupDashboardView ────────────────────────────────────────────────

function GroupDashboardView({
  selectedPersons,
  selectedCompetencias,
  selectedPuestos,
  discProfiles,
  groupArts,
  groupArtsStatus,
  discGap,
  discGapStatus,
}) {
  // Reusable ARTS donut from Home — texts + colors come from i18n dashboard.arts*
  const artsTexts = {
    donutTitle: I18n.t('dashboard.artsDonutTitle'),
    profiles: I18n.t('dashboard.profiles'),
    person: I18n.t('dashboard.artsPerson'),
    persons: I18n.t('dashboard.artsPersons'),
    artsLabels: {
      A: I18n.t('dashboard.artsLabels.A'),
      R: I18n.t('dashboard.artsLabels.R'),
      T: I18n.t('dashboard.artsLabels.T'),
      S: I18n.t('dashboard.artsLabels.S'),
    },
  };

  // NW-2504: real aggregation from Redux discGap (Enfoque A)
  const heatmapData = computeGroupHeatmapData(discGap, selectedCompetencias);

  const hasPuesto = selectedPuestos && selectedPuestos.length > 0;

  return (
    <div className="disc-group-dashboard">
      {/* Group header */}
      <div className="disc-user-card disc-user-card--group">
        <div className="disc-avatar disc-avatar--group">
          {selectedPersons.length}
        </div>
        <div className="disc-user-info">
          <div className="disc-user-name">{I18n.t('toolbox.discPanel.groupDashboardName')}</div>
          <div className="disc-user-role">
            {selectedPersons.length}{' '}
            {selectedPersons.length === 1
              ? I18n.t('toolbox.discPanel.person')
              : I18n.t('toolbox.discPanel.persons')}
          </div>
          <div className="disc-group-names">
            {selectedPersons.map((p, i) => (
              <span key={p.id}>{p.title}{i < selectedPersons.length - 1 ? ',' : ''}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Donut section — reuses the Home ARTS donut component for visual consistency.
          legendShowCount/legendHideEmpty are NW-2503-specific: show "A: 2" instead of "A"
          and hide axes with 0 profiles. Home keeps default behavior (always A/R/T/S). */}
      <ARTSPreviewDonut
        texts={artsTexts}
        colors={getArtsColors()}
        artsDistribution={groupArts}
        isLoading={groupArtsStatus?.loading}
        legendShowCount
        legendHideEmpty
      />

      {/* Heatmap section (NW-2504) — visible only when a puesto is selected */}
      {hasPuesto ? (
        <div className="disc-chart-card disc-heatmap-section">
          {/* Header */}
          <div className="disc-heatmap-header">
            <span className="disc-heatmap-title">{I18n.t('toolbox.discPanel.heatmap.title')}</span>
            <span className="disc-heatmap-count">
              {selectedCompetencias.length}{' '}
              {selectedCompetencias.length === 1
                ? I18n.t('toolbox.discPanel.competency')
                : I18n.t('toolbox.discPanel.competencies')}
              {' · '}
              {selectedPersons.length}{' '}
              {selectedPersons.length === 1
                ? I18n.t('toolbox.discPanel.person')
                : I18n.t('toolbox.discPanel.persons')}
            </span>
          </div>
          <div className="disc-heatmap-divider" />
          <div className="disc-heatmap-desc">
            {I18n.t('toolbox.discPanel.heatmap.description')}
          </div>

          {/* T9: loading / error / empty states */}
          {discGapStatus && discGapStatus.loading ? (
            <Loader />
          ) : discGap && discGap.status && discGap.status.error ? (
            <div className="disc-heatmap-error">
              {I18n.t('toolbox.discPanel.groupEmptyState')}
            </div>
          ) : discGap && discGap.persons && discGap.persons.length === 0 ? (
            <div className="disc-heatmap-empty">
              {I18n.t('toolbox.discPanel.groupEmptyState')}
            </div>
          ) : (
            <>
              {/* T4: Cells with SVG striped fill */}
              <div className="disc-heatmap-cells">
                {heatmapData.map((row) => {
                  const { bucket, color, patternId } = getHeatmapColorByScore(row.avg);
                  const factor = getCompetenciaDiscFactor(row.name_es || row.fullName);
                  const isNa = row.status === 'na';

                  // T6: tooltip text
                  const tooltipLines = isNa
                    ? [I18n.t('toolbox.discPanel.heatmap.noData')]
                    : [
                        I18n.t('toolbox.discPanel.heatmap.tooltipAvg', { avg: row.avg, count: row.count }),
                        I18n.t('toolbox.discPanel.heatmap.tooltipIdeal', { ideal: row.ideal }),
                      ];
                  const tooltipText = tooltipLines.join('\n');

                  return (
                    <div key={row.fullName} className="disc-heatmap-cell-col">
                      {/* Cell box: SVG with striped pattern or N/A grey */}
                      <div
                        className={`disc-heatmap-cell-box${isNa ? ' disc-heatmap-cell-box--na' : ''}`}
                        title={tooltipText}
                        style={
                          isNa
                            ? undefined
                            : {
                                backgroundColor: hexToRgba(color, 0.75),
                                backgroundImage: HEATMAP_STRIPE_BG,
                              }
                        }
                      >
                        {isNa ? (
                          <span className="disc-heatmap-cell-value disc-heatmap-cell-value--na">
                            {I18n.t('toolbox.discPanel.heatmap.noData')}
                          </span>
                        ) : (
                          <span className="disc-heatmap-cell-value">{row.avg}%</span>
                        )}
                      </div>

                      <div className="disc-heatmap-cell-meta">
                        {factor && (
                          <span
                            className="disc-heatmap-cell-factor"
                            title={`Factor DISC: ${getDiscLabel(factor)} (${DISC_TO_ARTS[factor]})`}
                          >
                            <span
                              className="disc-distribution-dot disc-distribution-dot--sm"
                              style={{ backgroundColor: DISC_COLORS[factor] }}
                            />
                            <span style={{ color: DISC_COLORS[factor], fontWeight: 700, fontSize: '9px' }}>
                              {DISC_TO_ARTS[factor]}
                            </span>
                          </span>
                        )}
                        <div className="disc-heatmap-cell-name" title={row.fullName}>
                          {row.fullName}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* T7: Legend — High → Mid → Low */}
              <div className="disc-heatmap-legend">
                {[
                  { color: DISC_COLORS.S, key: 'scaleHigh' },
                  { color: DISC_COLORS.I, key: 'scaleMid' },
                  { color: DISC_COLORS.D, key: 'scaleLow' },
                ].map(({ color: swatchColor, key: labelKey }) => (
                  <div key={labelKey} className="disc-heatmap-legend-item">
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        marginRight: 4,
                        flexShrink: 0,
                        backgroundColor: hexToRgba(swatchColor, 0.75),
                        backgroundImage: HEATMAP_STRIPE_BG,
                        borderRadius: 2,
                      }}
                    />
                    <span>{I18n.t(`toolbox.discPanel.heatmap.${labelKey}`)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        /* T6 / ADD-1c: optional hint when no puesto selected */
        selectedCompetencias.length > 0 && (
          <div className="disc-heatmap-requires-position">
            {I18n.t('toolbox.discPanel.heatmap.requiresPosition')}
          </div>
        )
      )}

      {/* T5: CompetencyDistribution — always visible when competencias selected, independent of puesto */}
      {selectedCompetencias.length > 0 && (
        <CompetencyDistribution selectedCompetencias={selectedCompetencias} />
      )}
    </div>
  );
}

// ── CompetencyDistribution ────────────────────────────────────────────

function CompetencyDistribution({ selectedCompetencias }) {
  // Count competencies per DISC factor
  const factorCounts = { D: 0, I: 0, S: 0, C: 0 };
  const factorCompetencies = { D: [], I: [], S: [], C: [] };
  selectedCompetencias.forEach((comp) => {
    const factor = getCompetenciaDiscFactor(comp.name_es || comp.title);
    if (factor) {
      factorCounts[factor]++;
      factorCompetencies[factor].push(comp.title);
    }
  });
  const total = selectedCompetencias.length;

  // Only show factors that have competencies in the detail grid
  const activeFactor = ARTS_ORDER.filter((key) => factorCounts[key] > 0);

  return (
    <div className="disc-chart-card disc-distribution-section">
      {/* Header */}
      <div className="disc-distribution-header">
        <span className="disc-distribution-title">{I18n.t('toolbox.discPanel.distribution.title')}</span>
        <span className="disc-distribution-count">
          {total}{' '}
          {total === 1
            ? I18n.t('toolbox.discPanel.competency')
            : I18n.t('toolbox.discPanel.competencies')}
        </span>
      </div>
      <div className="disc-distribution-divider" />
      <div className="disc-distribution-desc">
        {I18n.t('toolbox.discPanel.distribution.description')}
      </div>

      {/* Bars */}
      <div className="disc-distribution-bars">
        {(() => {
          const lrItems = ARTS_ORDER.map((key) => ({ key, count: factorCounts[key] || 0 }));
          const lrResult = total > 0
            ? largestRemainderRound(lrItems, total)
            : lrItems.map(({ key }) => ({ key, pct: 0 }));
          const pctByKey = Object.fromEntries(lrResult.map(({ key, pct }) => [key, pct]));
          return ARTS_ORDER.map((key) => {
          const count = factorCounts[key];
          const pct = pctByKey[key];
          return (
            <div key={key} className="disc-distribution-bar-row">
              <div className="disc-distribution-bar-label">
                <span className="disc-distribution-dot" style={{ backgroundColor: DISC_COLORS[key] }} />
                <span className="disc-distribution-letter" style={{ color: DISC_COLORS[key] }}>{DISC_TO_ARTS[key]}</span>
                <span className="disc-distribution-factor-name">{getDiscLabel(key)}</span>
              </div>
              <div className="disc-distribution-bar-track">
                <div
                  className="disc-distribution-bar-fill"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: DISC_COLORS[key],
                    minWidth: count > 0 ? '24px' : '0',
                  }}
                />
              </div>
              <div className="disc-distribution-bar-value">
                <span className="disc-distribution-bar-count" style={{ color: DISC_COLORS[key] }}>{count}</span>
                <span className="disc-distribution-bar-pct">({pct}%)</span>
              </div>
            </div>
          );
        });
        })()}
      </div>

      {/* Detail grid — render only factors with content, in ARTS order.
          Layout uses flex so the rendered items expand to fill the row width. */}
      {activeFactor.length > 0 && (
        <div className="disc-distribution-detail">
          <div className="disc-distribution-grid">
            {activeFactor.map((key) => (
              <div key={key} className="disc-distribution-grid-item">
                <div className="disc-distribution-grid-header">
                  <span className="disc-distribution-dot disc-distribution-dot--sm" style={{ backgroundColor: DISC_COLORS[key] }} />
                  <span className="disc-distribution-grid-label" style={{ color: DISC_COLORS[key] }}>
                    {DISC_TO_ARTS[key]} — {getDiscLabel(key)}
                  </span>
                </div>
                <div className="disc-distribution-grid-list">
                  {factorCompetencies[key].map((name) => (
                    <div key={name} className="disc-distribution-grid-comp">{name}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── ExportDropdown ────────────────────────────────────────────────────

function ExportDropdown({ onExportPng, onExportPdf, isExporting, variant = 'default' }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleClick = (handler) => {
    setOpen(false);
    handler();
  };

  return (
    <div className="disc-export-dropdown" ref={containerRef}>
      <button
        className={`disc-btn-export${variant === 'primary' ? ' disc-btn-export--primary' : ''}`}
        onClick={() => setOpen(!open)}
        disabled={isExporting}
      >
        {isExporting ? (
          <span className="disc-export-spinner" />
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" x2="12" y1="15" y2="3" />
          </svg>
        )}
        <span>
          {isExporting
            ? I18n.t('toolbox.discPanel.exporting')
            : I18n.t('toolbox.discPanel.export')}
        </span>
        {!isExporting && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}>
            <path d="m6 9 6 6 6-6" />
          </svg>
        )}
      </button>
      {open && !isExporting && (
        <div className="disc-export-menu">
          <button className="disc-export-option" onClick={() => handleClick(onExportPng)}>
            <span className="disc-export-option-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                <circle cx="9" cy="9" r="2" />
                <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
              </svg>
            </span>
            <span className="disc-export-option-text">
              <span className="disc-export-option-title">{I18n.t('toolbox.discPanel.exportAsPng')}</span>
              <span className="disc-export-option-desc">{I18n.t('toolbox.discPanel.exportAsPngDescription')}</span>
            </span>
          </button>
          <button className="disc-export-option" onClick={() => handleClick(onExportPdf)}>
            <span className="disc-export-option-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M12 18v-6" />
                <path d="m9 15 3 3 3-3" />
              </svg>
            </span>
            <span className="disc-export-option-text">
              <span className="disc-export-option-title">{I18n.t('toolbox.discPanel.exportAsPdf')}</span>
              <span className="disc-export-option-desc">{I18n.t('toolbox.discPanel.exportAsPdfDescription')}</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

// ── Export utilities ──────────────────────────────────────────────────

const EXPORT_COLOR_PROPS = [
  'color', 'backgroundColor', 'borderColor',
  'borderTopColor', 'borderRightColor', 'borderBottomColor', 'borderLeftColor',
  'outlineColor', 'textDecorationColor', 'fill', 'stroke',
  'boxShadow', 'caretColor',
];

function inlineComputedColors(srcRoot, cloneRoot) {
  const computed = window.getComputedStyle(srcRoot);
  EXPORT_COLOR_PROPS.forEach((prop) => {
    const val = computed.getPropertyValue(prop);
    if (val && val !== 'none' && val !== 'transparent') {
      cloneRoot.style[prop] = val;
    }
  });
  cloneRoot.style.fontFamily = computed.fontFamily;
  const srcChildren = srcRoot.children;
  const cloneChildren = cloneRoot.children;
  for (let i = 0; i < srcChildren.length; i += 1) {
    if (cloneChildren[i] instanceof HTMLElement) {
      inlineComputedColors(srcChildren[i], cloneChildren[i]);
    }
  }
}

async function captureElementToCanvas(element) {
  // Strategy: clone the off-screen export view, then capture each section
  // (`.disc-group-dashboard` + `.disc-single-view` per person) as its own
  // canvas via html2canvas. Each canvas → 1 PDF page. Slicing the
  // full-clone canvas was unreliable when group dashboard + single views
  // co-existed (off-screen rects drifted).
  const clone = element.cloneNode(true);
  inlineComputedColors(element, clone);
  clone.style.position = 'absolute';
  clone.style.left = '-99999px';
  clone.style.top = '0';
  document.body.appendChild(clone);

  // Force single-view wrappers to fill the export width. The .disc-single-view
  // SCSS caps max-width at 900px (or 1100px at xl) for the on-screen layout,
  // but html2canvas evaluates media queries against the actual viewport — so on
  // smaller laptops the export ends up with huge side margins. Override here.
  const singleViews = clone.querySelectorAll('.disc-single-view');
  singleViews.forEach((view) => {
    view.style.maxWidth = '100%';
    view.style.padding = '12px 16px';
  });

  // Bump text sizes for legibility in the export — applied to the cloned
  // DOM only, does NOT affect the on-screen view. Covers SVG chart text
  // (radar labels, axes, wheel %), card titles, and table/legend/HTML text
  // inside chart cards.
  const TEXT_BUMP_PX = 5;
  const svgTextNodes = clone.querySelectorAll('.disc-chart-card svg text, .disc-gap-section svg text');
  svgTextNodes.forEach((node) => {
    const currentFs = parseFloat(window.getComputedStyle(node).fontSize) || 12;
    node.style.fontSize = `${currentFs + TEXT_BUMP_PX}px`;
  });
  const htmlTextSelectors = [
    '.disc-chart-title',
    '.disc-user-name',
    '.disc-user-role',
    '.disc-user-campaign',
    '.disc-gap-subtitle',
    '.disc-gap-legend-item',
    '.disc-gap-factor-item',
    '.disc-gap-table-header > div',
    '.disc-gap-table-comp',
    '.disc-gap-table-center',
    '.disc-mini-bar-label',
    '.disc-mini-bar-value',
    '.disc-distribution-title',
    '.disc-distribution-count',
    '.disc-distribution-desc',
    '.disc-heatmap-title',
    '.disc-heatmap-count',
    '.disc-heatmap-desc',
    '.arts-preview-panel .chart-header p',
    '.disc-distribution-bar-label',
    '.disc-distribution-bar-value',
    '.disc-distribution-grid-label',
    '.disc-distribution-grid-comp',
    '.disc-compat-comp-name',
    '.disc-compat-percent',
    '.disc-compat-row-meta',
  ];
  clone.querySelectorAll(htmlTextSelectors.join(',')).forEach((node) => {
    const currentFs = parseFloat(window.getComputedStyle(node).fontSize) || 14;
    node.style.fontSize = `${currentFs + TEXT_BUMP_PX}px`;
  });

  // Constrain Recharts ResponsiveContainer charts to their card width.
  // Off-screen rendering can mis-measure container width, producing an SVG
  // wider than the parent card. Affected: Jerarquía Conductual BarChart
  // (`.disc-bar-card`) and Gap Chart de Competencias (`.disc-gap-section`).
  const constrainedCards = clone.querySelectorAll('.disc-bar-card, .disc-gap-section');
  constrainedCards.forEach((card) => {
    const responsive = card.querySelector('.recharts-responsive-container');
    if (!responsive) return;
    const cardStyle = window.getComputedStyle(card);
    const padX =
      parseFloat(cardStyle.paddingLeft || '0') +
      parseFloat(cardStyle.paddingRight || '0');
    const targetWidth = Math.max(0, card.clientWidth - padX);
    if (!targetWidth) return;
    responsive.style.width = `${targetWidth}px`;
    responsive.style.maxWidth = '100%';
    const svg = responsive.querySelector('svg');
    if (svg) {
      svg.setAttribute('width', String(targetWidth));
      svg.style.width = `${targetWidth}px`;
      svg.style.maxWidth = '100%';
    }
  });

  const CAPTURE_SCALE = 2;
  try {
    const groupView = clone.querySelector('.disc-group-dashboard');
    const singleViews = Array.from(clone.querySelectorAll('.disc-single-view'));
    const sliceElements = groupView ? [groupView, ...singleViews] : singleViews;
    // Fallback: if nothing matched, capture the whole clone as one page.
    const targets = sliceElements.length > 0 ? sliceElements : [clone];

    const pageCanvases = [];
    for (const target of targets) {
      // eslint-disable-next-line no-await-in-loop
      const pageCanvas = await html2canvas(target, {
        scale: CAPTURE_SCALE,
        useCORS: true,
        backgroundColor: '#f6f6f6',
        logging: false,
        windowWidth: target.scrollWidth,
        windowHeight: target.scrollHeight,
      });
      pageCanvases.push(pageCanvas);
    }

    return { pageCanvases, scale: CAPTURE_SCALE };
  } finally {
    document.body.removeChild(clone);
  }
}

// PNG export: combine all page canvases vertically into a single image so
// the PNG file matches what the user sees (group + each person stacked).
function downloadCanvasAsPng({ pageCanvases }, filename) {
  if (!pageCanvases || pageCanvases.length === 0) return;

  const combined = document.createElement('canvas');
  combined.width = Math.max(...pageCanvases.map((c) => c.width));
  combined.height = pageCanvases.reduce((acc, c) => acc + c.height, 0);
  const ctx = combined.getContext('2d');
  ctx.fillStyle = '#f6f6f6';
  ctx.fillRect(0, 0, combined.width, combined.height);
  let y = 0;
  pageCanvases.forEach((c) => {
    const offsetX = Math.round((combined.width - c.width) / 2);
    ctx.drawImage(c, offsetX, y);
    y += c.height;
  });

  const link = document.createElement('a');
  link.download = `${filename}.png`;
  link.href = combined.toDataURL('image/png');
  link.click();
}

// PDF export: every page canvas (group dashboard, single user view) becomes
// one A4 portrait page. Image is scaled proportionally (fit-to-page) and
// centered, so the same PDF layout renders identically on any device.
function downloadCanvasAsPdf({ pageCanvases, scale }, filename) {
  if (!pageCanvases || pageCanvases.length === 0) return;

  // A4 portrait in points (jsPDF default unit). 1pt = 1/72 inch.
  const PAGE_W = 595;
  const PAGE_H = 842;
  const PAGE_PADDING = 24;
  const MAX_W = PAGE_W - PAGE_PADDING * 2;
  const MAX_H = PAGE_H - PAGE_PADDING * 2;
  const JPEG_QUALITY = 0.92;

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4',
  });

  pageCanvases.forEach((pageCanvas, idx) => {
    // Unscaled dimensions (canvas was captured at scale=CAPTURE_SCALE).
    const sliceW = pageCanvas.width / scale;
    const sliceH = pageCanvas.height / scale;
    // Fit-to-page: keep aspect ratio, never overflow.
    // Horizontally centered, top-aligned (content starts at top with padding,
    // not vertically centered — looks more natural for reports).
    const ratio = Math.min(MAX_W / sliceW, MAX_H / sliceH);
    const drawW = sliceW * ratio;
    const drawH = sliceH * ratio;
    const offsetX = (PAGE_W - drawW) / 2;
    const offsetY = PAGE_PADDING;

    if (idx > 0) pdf.addPage('a4', 'portrait');

    pdf.setFillColor(246, 246, 246);
    pdf.rect(0, 0, PAGE_W, PAGE_H, 'F');

    // JPEG 0.92 keeps file size manageable and is visually lossless for charts.
    const imgData = pageCanvas.toDataURL('image/jpeg', JPEG_QUALITY);
    pdf.addImage(imgData, 'JPEG', offsetX, offsetY, drawW, drawH);
  });

  pdf.save(`${filename}.pdf`);
}

// ── DISCResultsPanel (main export) ────────────────────────────────────

function DISCResultsPanel({
  selectedPersons,
  selectedCompetencias,
  selectedPuestos,
  discProfiles,
  loading,
  discGap,
  discGapStatus,
  getDiscGap,
  groupArts,
  groupArtsStatus,
  getGroupArts,
}) {
  const [showGroupDashboard, setShowGroupDashboard] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    if (
      selectedPersons && selectedPersons.length > 0 &&
      selectedPuestos && selectedPuestos.length > 0 &&
      selectedCompetencias && selectedCompetencias.length > 0 &&
      getDiscGap
    ) {
      const idResults = selectedPersons.map((p) => p.id_results);
      const positionId = selectedPuestos[0].id;
      const skillIds = selectedCompetencias.map((c) => c.id);
      getDiscGap(idResults, positionId, skillIds);
    }
  }, [selectedPersons, selectedPuestos, selectedCompetencias]);

  // Dispatch getGroupArts when entering the group dashboard view (NW-2503)
  // Uses p.id_results (NOT p.id) — maps to nwm_result.id in the backend
  useEffect(() => {
    if (
      showGroupDashboard &&
      selectedPersons &&
      selectedPersons.length >= 2 &&
      selectedPersons.length <= 5 &&
      getGroupArts
    ) {
      const idResults = selectedPersons.map((p) => p.id_results);
      getGroupArts(idResults);
    }
  }, [showGroupDashboard, selectedPersons]);

  if (!selectedPersons || selectedPersons.length === 0) {
    return null;
  }

  const isSingle = selectedPersons.length === 1;

  const handleExport = async (format) => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      if (!exportRef.current) return;
      // Brief frame yield so the spinner shows before the synchronous
      // html2canvas work blocks the main thread.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const captureResult = await captureElementToCanvas(exportRef.current);
      // Filename: Informe_DISC_YYYY-MM-DD_HH-MM-SS — same prefix for
      // individual and group exports (NW-2529).
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const datePart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      const timePart = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
      const baseName = I18n.t('toolbox.discPanel.exportFileName');
      const filename = `${baseName}_${datePart}_${timePart}`;
      if (format === 'png') {
        downloadCanvasAsPng(captureResult, filename);
      } else {
        downloadCanvasAsPdf(captureResult, filename);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPng = () => handleExport('png');
  const handleExportPdf = () => handleExport('pdf');

  return (
    <div className={`disc-results-panel${isSingle ? '' : ' disc-results-panel--multi'}`}>
      {/* Header bar */}
      <div className="disc-panel-header">
        {showGroupDashboard ? (
          <>
            <div className="disc-panel-header-left">
              <button
                className="disc-btn-back"
                onClick={() => setShowGroupDashboard(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12 19-7-7 7-7" />
                  <path d="M19 12H5" />
                </svg>
              </button>
              <h2 className="disc-panel-title">{I18n.t('toolbox.discPanel.titleGroup')}</h2>
            </div>
            <div className="disc-panel-header-actions">
              <ExportDropdown
                onExportPng={handleExportPng}
                onExportPdf={handleExportPdf}
                isExporting={isExporting}
                variant="primary"
              />
            </div>
          </>
        ) : (
          <>
            <h2 className="disc-panel-title">{I18n.t('toolbox.discPanel.titleSingle')}</h2>
            <div className="disc-panel-header-actions">
              <ExportDropdown
                onExportPng={handleExportPng}
                onExportPdf={handleExportPdf}
                isExporting={isExporting}
              />
              {/* Button guard (D2): hidden for 0/1 person, enabled for 2-5, disabled for >5 or no competencia */}
              {selectedPersons.length >= 2 && (
                <button
                  className="disc-btn-dashboard"
                  onClick={() => setShowGroupDashboard(true)}
                  disabled={selectedPersons.length > 5 || selectedCompetencias.length < 1}
                  aria-disabled={selectedPersons.length > 5 || selectedCompetencias.length < 1}
                  title={
                    selectedPersons.length > 5
                      ? I18n.t('toolbox.discPanel.groupGuard.tooMany')
                      : selectedCompetencias.length < 1
                      ? I18n.t('toolbox.discPanel.groupGuard.requiresCompetencia')
                      : undefined
                  }
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="7" height="9" x="3" y="3" rx="1" />
                    <rect width="7" height="5" x="14" y="3" rx="1" />
                    <rect width="7" height="9" x="14" y="12" rx="1" />
                    <rect width="7" height="5" x="3" y="16" rx="1" />
                  </svg>
                  <span>{I18n.t('toolbox.discPanel.viewGroupDashboard')}</span>
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Scrollable content */}
      <div className="disc-panel-scroll">
        <div className="disc-panel-content">
          {loading ? (
            <div className="disc-panel-loader">
              <Loader />
            </div>
          ) : isSingle ? (
            <SingleUserView
              person={selectedPersons[0]}
              selectedCompetencias={selectedCompetencias || []}
              selectedPuestos={selectedPuestos || []}
              discProfiles={discProfiles || []}
              discGap={discGap}
            />
          ) : (
            <>
              {showGroupDashboard ? (
                <GroupDashboardView
                  selectedPersons={selectedPersons}
                  selectedCompetencias={selectedCompetencias || []}
                  selectedPuestos={selectedPuestos || []}
                  discProfiles={discProfiles || []}
                  groupArts={groupArts}
                  groupArtsStatus={groupArtsStatus}
                  discGap={discGap}
                  discGapStatus={discGapStatus}
                />
              ) : (
                <div className="disc-multi-scroll">
                  {selectedPersons.map((person) => (
                    <MultiUserColumn
                      key={person.id}
                      person={person}
                      discProfiles={discProfiles || []}
                      selectedCompetencias={selectedCompetencias || []}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Hidden export view — always rendered off-screen so Recharts has
          time to measure dimensions properly via ResizeObserver. Otherwise
          the radar chart collapses on first render when rendered conditionally.
          Branches between GroupDashboardView (group export) and SingleUserView per person. */}
      <div
        ref={exportRef}
        aria-hidden="true"
        className="disc-results-panel disc-export-view"
        style={{
          position: 'absolute',
          left: '-99999px',
          top: 0,
          boxSizing: 'border-box',
          width: '1164px',
          height: 'auto',
          padding: '32px',
          margin: 0,
          border: 'none',
          borderRadius: 0,
          backgroundColor: '#f6f6f6',
          pointerEvents: 'none',
        }}
      >
        <div className="disc-panel-content" style={{ width: '100%' }}>
          {showGroupDashboard ? (
            <GroupDashboardView
              selectedPersons={selectedPersons}
              selectedCompetencias={selectedCompetencias || []}
              selectedPuestos={selectedPuestos || []}
              discProfiles={discProfiles || []}
              groupArts={groupArts}
              groupArtsStatus={groupArtsStatus}
              discGap={discGap}
              discGapStatus={discGapStatus}
            />
          ) : (
            selectedPersons.map((person) => (
              <SingleUserView
                key={person.id}
                person={person}
                selectedCompetencias={selectedCompetencias || []}
                selectedPuestos={selectedPuestos || []}
                discProfiles={discProfiles || []}
                discGap={discGap}
                wheelSize={260}
                hideGapChart={!isSingle}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default DISCResultsPanel;
